import { type ShortUrl } from '@air/api';
import { type ShortUrlResponse } from '@air/api/types';

type ShortUrlUpdatePayload = Parameters<typeof ShortUrl.update>[0];

export type ShortUrlMutationPresentationSettings =
  | ShortUrlResponse['presentationSettings']
  | ShortUrlUpdatePayload['presentationSettings'];

const CUSTOM_COLOR_ROLES = [
  'headerBgColor',
  'buttonBgColor',
  'headlineBgColor',
  'pageBgColor',
  'accentBgColor',
] as const;

type CustomColorRole = (typeof CUSTOM_COLOR_ROLES)[number];

type ShortUrlUpdateCustomColors = NonNullable<
  NonNullable<ShortUrlUpdatePayload['presentationSettings']>['customColors']
>;

type ShortUrlMutationCustomColor = NonNullable<
  NonNullable<NonNullable<ShortUrlMutationPresentationSettings>['customColors']>[CustomColorRole]
>;

const getShortUrlUpdateCustomColor = (
  customColor: ShortUrlMutationCustomColor,
): NonNullable<ShortUrlUpdateCustomColors[CustomColorRole]> => {
  switch (customColor.type) {
    case 'hex':
      return {
        type: 'hex',
        hex: customColor.hex,
      };
    case 'contextSource':
      // the read shape includes a server-resolved hex; the write shape must stay a
      // pure brand-kit reference (the full id triple — the backend rejects a value id
      // alone) so the server re-resolves the color on read
      return {
        type: 'contextSource',
        contextSourceId: customColor.contextSourceId,
        contextSourceFieldId: customColor.contextSourceFieldId,
        contextSourceFieldValueId: customColor.contextSourceFieldValueId,
      };
  }
};

const CUSTOM_FONT_ROLES = ['bodyFont', 'headlineFont'] as const;

type CustomFontRole = (typeof CUSTOM_FONT_ROLES)[number];

type ShortUrlUpdateCustomFonts = NonNullable<NonNullable<ShortUrlUpdatePayload['presentationSettings']>['customFonts']>;

type ShortUrlMutationCustomFontValue = NonNullable<
  NonNullable<NonNullable<ShortUrlMutationPresentationSettings>['customFonts']>[CustomFontRole]
>;

const normalizeCustomFontValue = (
  customFont: ShortUrlMutationCustomFontValue,
): NonNullable<ShortUrlUpdateCustomFonts[CustomFontRole]> => {
  switch (customFont.type) {
    case 'system':
      return {
        type: 'system',
        fontFamily: customFont.fontFamily,
        ...(customFont.variant ? { variant: customFont.variant } : {}),
      };
    // TODO(COR-1951): asset fonts are out of scope for this pass (no font-upload UI yet).
    case 'asset':
      return {
        type: 'asset',
        assetId: customFont.assetId,
        versionId: customFont.versionId,
        fontFamily: customFont.fontFamily,
        ...(customFont.variant ? { variant: customFont.variant } : {}),
      };
    // The read shape includes server-resolved `fontFamily`/`variant`/`url`; the write shape must
    // stay a pure brand-kit reference (the full id triple — the backend rejects a value id alone)
    // so the server re-resolves the font on read (mirrors the customColors contextSource).
    case 'contextSource':
      return {
        type: 'contextSource',
        contextSourceId: customFont.contextSourceId,
        contextSourceFieldId: customFont.contextSourceFieldId,
        contextSourceFieldValueId: customFont.contextSourceFieldValueId,
      };
  }
};

export const getShortUrlUpdatePresentationSettings = (
  presentationSettings: ShortUrlMutationPresentationSettings,
): ShortUrlUpdatePayload['presentationSettings'] => {
  if (presentationSettings == null) {
    return presentationSettings;
  }

  const { coverImage, customColors, customFonts, customLogo, ...rest } = presentationSettings;
  const updatePresentationSettings: NonNullable<ShortUrlUpdatePayload['presentationSettings']> = { ...rest };

  if (customColors) {
    const updateCustomColors: ShortUrlUpdateCustomColors = {};

    for (const role of CUSTOM_COLOR_ROLES) {
      const customColor = customColors[role];

      if (customColor) {
        updateCustomColors[role] = getShortUrlUpdateCustomColor(customColor);
      }
    }

    updatePresentationSettings.customColors = updateCustomColors;
  }

  if (customLogo === null) {
    // PresentationSettings is shallow-PATCH: an explicit null clears the stored value, while an
    // absent key persists it. Pass the clear through instead of dropping it on a truthy guard.
    updatePresentationSettings.customLogo = null;
  } else if (customLogo) {
    switch (customLogo.type) {
      case 'asset':
        updatePresentationSettings.customLogo = {
          type: 'asset',
          assetId: customLogo.assetId,
          versionId: customLogo.versionId,
        };
        break;
      case 'contextSource':
        updatePresentationSettings.customLogo = {
          type: 'contextSource',
          contextSourceId: customLogo.contextSourceId,
          contextSourceFieldId: customLogo.contextSourceFieldId,
          contextSourceFieldValueId: customLogo.contextSourceFieldValueId,
        };
        break;
    }
  }

  if (customFonts === null) {
    // Shallow-PATCH clear: pass the explicit null through so the server clears the stored fonts.
    updatePresentationSettings.customFonts = null;
  } else if (customFonts) {
    const updateCustomFonts: ShortUrlUpdateCustomFonts = {};

    for (const role of CUSTOM_FONT_ROLES) {
      const customFont = customFonts[role];

      if (customFont) {
        updateCustomFonts[role] = normalizeCustomFontValue(customFont);
      }
    }

    // `customFonts` is wholesale-replaced on the server when present, so writing `{}` would clear
    // the stored fonts; only an explicit null clear is intentional, so drop empty objects instead.
    if (Object.keys(updateCustomFonts).length > 0) {
      updatePresentationSettings.customFonts = updateCustomFonts;
    }
  }

  if (coverImage === null) {
    // Shallow-PATCH clear: pass the explicit null through so the server clears the stored cover.
    updatePresentationSettings.coverImage = null;
  } else if (coverImage?.assetId && coverImage.versionId && coverImage.region) {
    updatePresentationSettings.coverImage = {
      assetId: coverImage.assetId,
      region: coverImage.region,
      versionId: coverImage.versionId,
    };
  }

  return updatePresentationSettings;
};
